import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Alert,
  ButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { memo } from 'react';
import type { GroupMemberWithProfile, MemberFilter } from '../hooks/useGroupMembers';
import UserAvatar from '../../../components/UserAvatar';

export type GroupMembersTabProps = {
  readonly members: GroupMemberWithProfile[];
  readonly filteredMembers: GroupMemberWithProfile[];
  readonly memberCount: {
    readonly total: number;
    readonly active: number;
    readonly pending: number;
  };
  readonly loading: boolean;
  readonly error: Error | null;
  readonly currentFilter: MemberFilter;
  readonly approvingMemberIds: Set<string>;
  readonly denyingMemberIds: Set<string>;
  readonly onFilterChange: (filter: MemberFilter) => void;
  readonly onApproveMember: (memberId: string) => Promise<void>;
  readonly onDenyMember: (memberId: string) => Promise<void>;
  readonly isOrganizer: boolean;
};

const GroupMembersTab = memo<GroupMembersTabProps>(
  ({
    members,
    filteredMembers,
    memberCount,
    loading,
    error,
    currentFilter,
    approvingMemberIds,
    denyingMemberIds,
    onFilterChange,
    onApproveMember,
    onDenyMember,
    isOrganizer,
  }) => {
    const getMemberStatusColor = (status: string) => {
      switch (status) {
        case 'active':
          return 'success';
        case 'pending':
          return 'warning';
        default:
          return 'default';
      }
    };

    const getMemberStatusLabel = (status: string) => {
      switch (status) {
        case 'active':
          return 'Active';
        case 'pending':
          return 'Pending';
        default:
          return status;
      }
    };

    const renderMemberCard = (member: GroupMemberWithProfile) => (
      <Card key={member.uid} variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <UserAvatar
                displayName={member.profile.displayName}
                photoURL={member.profile.photoURL || null}
                email={member.profile.email}
                sx={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {member.profile.displayName}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    size="small"
                    label={getMemberStatusLabel(member.status)}
                    color={getMemberStatusColor(member.status)}
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Role: {member.role}
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            {/* Action buttons for pending members (organizer only) */}
            {isOrganizer && member.status === 'pending' && (
              <Stack direction="row" spacing={1}>
                <Tooltip title="Approve membership">
                  <IconButton
                    color="success"
                    size="small"
                    onClick={() => void onApproveMember(member.uid)}
                    disabled={
                      approvingMemberIds.has(member.uid) ||
                      denyingMemberIds.has(member.uid)
                    }
                  >
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Deny membership">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => void onDenyMember(member.uid)}
                    disabled={
                      approvingMemberIds.has(member.uid) ||
                      denyingMemberIds.has(member.uid)
                    }
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
    );

    if (!isOrganizer) {
      return (
        <Alert severity="info">
          Only organizers can view and manage group membership.
        </Alert>
      );
    }

    if (error) {
      return <Alert severity="error">Failed to load members: {error.message}</Alert>;
    }

    return (
      <Stack spacing={3}>
        {/* Header with member counts and filters */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Group Members
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {memberCount.total} total • {memberCount.active} active •{' '}
              {memberCount.pending} pending
            </Typography>
          </Box>

          {/* Filter buttons */}
          <ButtonGroup size="small" variant="outlined">
            <Button
              variant={currentFilter === 'all' ? 'contained' : 'outlined'}
              onClick={() => onFilterChange('all')}
              startIcon={<FilterListIcon />}
            >
              All ({memberCount.total})
            </Button>
            <Button
              variant={currentFilter === 'active' ? 'contained' : 'outlined'}
              onClick={() => onFilterChange('active')}
            >
              Active ({memberCount.active})
            </Button>
            <Button
              variant={currentFilter === 'pending' ? 'contained' : 'outlined'}
              onClick={() => onFilterChange('pending')}
            >
              Pending ({memberCount.pending})
            </Button>
          </ButtonGroup>
        </Stack>

        {/* Members list */}
        {loading ? (
          <Typography color="text.secondary">Loading members...</Typography>
        ) : filteredMembers.length === 0 ? (
          <Box textAlign="center" py={4}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No members found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentFilter === 'pending'
                ? 'No pending membership requests'
                : currentFilter === 'active'
                  ? 'No active members yet'
                  : 'No members in this group yet'}
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>{filteredMembers.map(renderMemberCard)}</Stack>
        )}
      </Stack>
    );
  },
);

GroupMembersTab.displayName = 'GroupMembersTab';

export default GroupMembersTab;
